"use client";

import { Button } from "@universe/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@universe/ui/components/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@universe/ui/components/drawer";
import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  showDefaultFooter?: boolean;
  onInteractOutside?: (event: Event) => void;
  maxHeight?: string;
  drawerDirection?: "top" | "right" | "bottom" | "left";
  preventClose?: boolean;
  headerVariant?: "default" | "white";
}

export function ResponsiveDialog({
  open,
  onOpenChange,
  trigger,
  title,
  children,
  footer,
  showCloseButton = true,
  className,
  contentClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  showDefaultFooter = true,
  onInteractOutside,
  maxHeight = "90dvh",
  drawerDirection = "right",
  preventClose,
  headerVariant = "default",
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile();
  const tCommon = useTranslations("common");

  const defaultFooter = showDefaultFooter ? (
    preventClose ? (
      isMobile ? (
        <Button size="lg" className="!h-10 text-base" disabled>
          {tCommon("close")}
        </Button>
      ) : (
        <Button disabled className="!h-11 text-base !w-full rounded-lg h-10">
          {tCommon("close")}
        </Button>
      )
    ) : isMobile ? (
      <DrawerClose asChild>
        <Button size="lg" className="!h-10 text-base">
          {tCommon("close")}
        </Button>
      </DrawerClose>
    ) : (
      <DialogClose asChild>
        <Button className="!h-11 text-base !w-full rounded-lg h-10">
          {tCommon("close")}
        </Button>
      </DialogClose>
    )
  ) : null;

  const footerContent = footer || defaultFooter;

  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {}
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          onInteractOutside={
            onInteractOutside ||
            ((e) => {
              e.preventDefault();
            })
          }
          className={cn(
            "rounded-2xl !max-w-[520px] !overflow-x-visible [&>[data-slot='dialog-close']]:hidden !overflow-hidden",
            `!max-h-[${maxHeight}]`,
            contentClassName
          )}
        >
          <DialogHeader
            className={cn(
              headerVariant === "white"
                ? "bg-white pt-1 pb-4 -mx-6 px-6 justify-center"
                : "bg-brand-primary/5 pt-1 pb-4 -mx-6 px-6 bg-gradient-to-t from-brand-primary/10 to-white justify-center",
              headerClassName
            )}
          >
            {title && <DialogTitle>{title}</DialogTitle>}
            {showCloseButton &&
              (preventClose ? (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-6 absolute right-5 top-5"
                  disabled
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">{tCommon("close")}</span>
                </Button>
              ) : (
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-6 w-6 absolute right-5 top-5"
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">{tCommon("close")}</span>
                  </Button>
                </DialogClose>
              ))}
          </DialogHeader>

          <div
            className={cn(
              "px-1 pb-3 space-y-6 overflow-y-auto mx-[-12px] px-4",
              `max-h-[calc(${maxHeight}-220px)]`,
              bodyClassName
            )}
          >
            {children}
          </div>

          {}
          {footerContent && (
            <DialogFooter
              className={cn(
                "pt-4 flex !justify-center !items-center",
                footerClassName
              )}
            >
              {footerContent}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile version (Drawer)
  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={drawerDirection}>
      {}
      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
      <DrawerContent
        className={cn(
          "!w-full rounded-none lg:rounded-xl overflow-hidden [&>[data-slot='dialog-close']]:hidden",
          className
        )}
        onInteractOutside={
          onInteractOutside ||
          ((e) => {
            e.preventDefault();
          })
        }
      >
        <DrawerHeader
          className={cn(
            headerVariant === "white"
              ? "bg-white pt-4.5 lg:pt-0 h-[58px]"
              : "bg-brand-primary/5 pt-4.5 lg:pt-0 h-[58px] bg-gradient-to-t from-brand-primary/10 to-white",
            headerClassName,
            title && "h-auto"
          )}
        >
          {title && <DrawerTitle>{title}</DrawerTitle>}
          {showCloseButton &&
            (preventClose ? (
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 absolute right-4 top-4.5"
                disabled
              >
                <X className="h-3 w-3" />
                <span className="sr-only">{tCommon("close")}</span>
              </Button>
            ) : (
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-6 w-6 absolute right-4 top-4.5"
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">{tCommon("close")}</span>
                </Button>
              </DrawerClose>
            ))}
        </DrawerHeader>

        <div
          className={cn(
            "p-4 space-y-4 overflow-y-auto max-h-[calc(100dvh-150px)] mx-[-12px] px-4",
            bodyClassName
          )}
        >
          {children}
        </div>

        {}
        {footerContent && (
          <DrawerFooter className={cn("!pt-0", footerClassName)}>
            {footerContent}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
